import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { QuotationProvider } from '@euler/app/flows/task/pages/quotation/components/QuotationContext';
import { QuotationDiscountScreen } from '@euler/app/flows/task/pages/quotation/DiscountScreen';
import { useQuotationCatalog } from '@euler/app/flows/task/pages/quotation/functions/useQuotationCatalog';
import { QuotationCatalogPage } from '@euler/app/flows/task/pages/quotation/QuotationCatalogPage';
import { QuotationCatalogSearchPage } from '@euler/app/flows/task/pages/quotation/QuotationCatalogSearchPage';
import { QuotationFormPage } from '@euler/app/flows/task/pages/quotation/QuotationFormPage';
import { SettlementFormPage } from '@euler/app/flows/task/pages/quotation/SettlementFormPage';
import { wrapTaskNavigatorScreen } from '@euler/app/flows/task/wrapTaskNavigatorScreen';
import { Center } from '@euler/components/Center';
import { ErrorFallback } from '@euler/components/error';
import { MultilineHeaderTitleFactory } from '@euler/components/MultilineHeaderTitle';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { useNavigation } from '@react-navigation/native';
import {
  createStackNavigator,
  TransitionPresets,
} from '@react-navigation/stack';
import { useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';

const Stack = createStackNavigator();

const Screens = {
  _main: QuotationFormPage,
  _catalog: QuotationCatalogPage,
  _search: QuotationCatalogSearchPage,
  _discount: QuotationDiscountScreen,
  _settlementForm: SettlementFormPage,
};

export type QuotationNavParams = ScreenToParams<typeof Screens>;

export const QuotationFlow = wrapTaskNavigatorScreen(
  () => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const { catalog, loading, error, invalidate } = useQuotationCatalog();
    const { taskManager } = useTaskContext();
    const navigation = useNavigation();

    useEffect(() => {
      const removeListener = navigation.addListener('beforeRemove', () => {
        taskManager.quotationManager.stopObserveChanges();
      });
      return removeListener;
    }, [navigation, taskManager.quotationManager]);

    const SubTitle = useMemo(
      () =>
        MultilineHeaderTitleFactory({
          subTitleStyle: { fontFamily: FontFamily.NotoSans.Medium },
        }),
      [],
    );

    if (loading || error) {
      return error ? (
        <ErrorFallback error={error} resetError={invalidate} />
      ) : (
        <Center
          css={`
            flex: 1;
          `}
        >
          <DefaultLoader />
        </Center>
      );
    }

    return (
      <QuotationProvider catalog={catalog}>
        <Stack.Navigator
          initialRouteName="_main"
          screenOptions={{
            ...TransitionPresets.SlideFromRightIOS,
            detachPreviousScreen: false,
            headerMode: 'float',
            headerTitleAlign: 'center',
            headerBackTitle: ' ',
            headerTitleStyle: {
              fontSize: rpx(18),
              fontFamily: FontFamily.NotoSans.Bold,
            },
            cardStyle: {
              backgroundColor: theme.page.card.backgroundColor,
            },
            headerTitle: SubTitle,
            headerBackImage: () => <BackIcon />,
          }}
        >
          {spreadScreens(Stack.Screen, Screens)}
        </Stack.Navigator>
      </QuotationProvider>
    );
  },
  {
    headerShown: false,
  },
  { dismissible: false, aside: true },
);
